import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { ApiError } from '../../api/client'
import {
  autoCommentsListQuery,
  autoCommentsShellQuery,
  changeAutoCommentStatus,
  createAutoComment,
  refreshAutoCommentsCron,
  removeAutoComment,
  updateAutoComment,
  type AutoComment,
} from '../../api/settingsEaseedeskAutoComments'
import { LoadFailed } from '../../ui/LoadFailed'
import { Modal } from '../../ui/Modal'
import { toast } from '../../ui/notify'
import { Spinner } from '../../ui/Spinner'

/**
 * Settings → Easeedesk → Auto comments.
 *
 * Element cu element fata de `version-3/easeedesk_settings/comments/
 * auto_comments/index.php` + `list.php`, si gest cu gest fata de `index-1.js`
 * si `list-1.js`: tabelul, comutatorul care salveaza singur, cele doua ferestre
 * si butonul „Refresh cron".
 *
 * CASUTA DE TIPURI E UNA OBISNUITA, cu `multiple` - vezi lamurirea de la
 * notificarile agentului: `SelectPicker` din v4 stie deocamdata o singura
 * alegere.
 */
export function AutoCommentsPage() {
  const queryClient = useQueryClient()
  const shell = useQuery(autoCommentsShellQuery())
  const list = useQuery(autoCommentsListQuery())
  const [creating, setCreating] = useState(false)
  const [edited, setEdited] = useState<AutoComment | null>(null)

  const reload = () => {
    void queryClient.invalidateQueries({ queryKey: ['settings', 'easeedesk', 'auto_comments'] })
  }

  const remove = useMutation({
    mutationFn: (id: number) => removeAutoComment(id),
    onSuccess: () => {
      toast('Auto comment removed.', 'Success')
      reload()
    },
    onError: () => toast('Something went wrong.', 'Error', 'error'),
  })

  const status = useMutation({
    mutationFn: ({ id, on }: { id: number; on: boolean }) => changeAutoCommentStatus(id, on),
    onSuccess: () => reload(),
    onError: () => toast('Something went wrong.', 'Error', 'error'),
  })

  const cron = useMutation({
    mutationFn: () => refreshAutoCommentsCron(),
    onSuccess: (answer) =>
      answer.cron === 'failed'
        ? toast(answer.cron_message || 'The cron server did not answer.', 'Error', 'error')
        : toast('Configuration build successfully.', 'Success'),
    onError: () => toast('Something went wrong.', 'Error', 'error'),
  })

  if (shell.error instanceof ApiError && shell.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (shell.error) {
    return <LoadFailed what="Auto comments" error={shell.error} onRetry={() => void shell.refetch()} />
  }
  if (!shell.data) return <Spinner />

  const comments = list.data?.auto_comments ?? []

  return (
    <div>
      <h4>
        <strong>Auto Comments</strong> types
      </h4>
      <label className="color" style={{ marginBottom: 0 }}>
        Manage, create or edit auto comments
      </label>
      <button
        type="button"
        className="btn btn-primary pull-right me-2 refresh-cron"
        disabled={cron.isPending}
        onClick={() => cron.mutate()}
      >
        <i className="fas fa-sync-alt" aria-hidden="true"></i> Refresh cron
      </button>
      <button type="button" className="btn btn-success pull-right me-2" onClick={() => setCreating(true)}>
        <i className="fas fa-plus" aria-hidden="true"></i> Add new auto comment
      </button>
      <hr />

      <div className="comments-list">
        {list.error ? (
          <LoadFailed what="The auto comments" error={list.error} onRetry={() => void list.refetch()} />
        ) : !list.data ? (
          <Spinner />
        ) : (
          <table className="table table-hover">
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>Comment</th>
                <th style={{ textAlign: 'left' }} width="150">
                  Ticket inactivity time
                </th>
                <th style={{ textAlign: 'left' }} width="200">
                  Ticket types
                </th>
                <th width="100">Status</th>
                <th width="100">Actions</th>
              </tr>
            </thead>
            <tbody>
              {comments.length === 0 && (
                <tr>
                  <td colSpan={5} className="text-center text-muted">
                    No auto comments yet. One writes itself on a ticket that has been quiet for the number of minutes
                    you set.
                  </td>
                </tr>
              )}
              {comments.map((comment) => (
                <tr key={comment.ID}>
                  <td>{comment.comment}</td>
                  <td>
                    <span className="label label-info">{comment.ticket_inactivity_time} minutes</span>
                  </td>
                  <td>{comment.ticket_types.length} ticket types</td>
                  <td style={{ textAlign: 'center' }}>
                    <div className="change_comment_status" style={{ marginLeft: 20 }}>
                      <input
                        type="checkbox"
                        checked={comment.active === 1}
                        onChange={(e) => status.mutate({ id: comment.ID, on: e.target.checked })}
                      />
                    </div>
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <div className="btn-group" role="group" aria-label="controls">
                      <button
                        type="button"
                        className="btn btn-danger btn-xs delete-comment"
                        onClick={() => {
                          if (!window.confirm('Are you sure?')) return
                          remove.mutate(comment.ID)
                        }}
                      >
                        <i className="fas fa-trash" aria-hidden="true"></i>
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary btn-xs edit-status"
                        onClick={() => setEdited(comment)}
                      >
                        <i className="fas fa-pencil-alt" aria-hidden="true"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <AutoCommentModal
        open={creating}
        comment={null}
        types={shell.data.ticket_types}
        onClose={() => setCreating(false)}
        onSaved={reload}
      />
      <AutoCommentModal
        open={edited !== null}
        comment={edited}
        types={shell.data.ticket_types}
        onClose={() => setEdited(null)}
        onSaved={reload}
      />
    </div>
  )
}

/** `newAutoCommentModal` si `editAutoCommentModal`: text, minute, tipuri. */
export function AutoCommentModal({
  open,
  comment,
  types,
  onClose,
  onSaved,
}: {
  open: boolean
  comment: AutoComment | null
  types: { ID: number; type_name: string }[]
  onClose(): void
  onSaved(): void
}) {
  const [text, setText] = useState('')
  const [minutes, setMinutes] = useState('')
  const [chosen, setChosen] = useState<number[]>([])

  useEffect(() => {
    if (!open) return
    setText(comment?.comment ?? '')
    setMinutes(comment === null ? '' : String(comment.ticket_inactivity_time))
    setChosen(comment?.ticket_types ?? [])
  }, [open, comment])

  const save = useMutation({
    mutationFn: () =>
      comment === null
        ? createAutoComment(text.trim(), Number(minutes), chosen)
        : updateAutoComment(comment.ID, text.trim(), Number(minutes), chosen),
    onSuccess: () => {
      toast(comment === null ? 'Auto comment created.' : 'Auto comment successfully updated.', 'Success')
      onClose()
      onSaved()
    },
    onError: (error) =>
      toast(error instanceof ApiError && error.message ? error.message : 'Something went wrong.', 'Error', 'error'),
  })

  return (
    <Modal
      id={comment === null ? 'newAutoCommentModal' : 'editAutoCommentModal'}
      labelledBy={comment === null ? 'newAutoCommentModalLabel' : 'editAutoCommentModalLabel'}
      open={open}
      onClose={onClose}
      title={comment === null ? 'New auto comment' : 'Edit auto comment'}
    >
      <form
        id={comment === null ? 'new-auto-comment-form' : 'edit-auto-comment-form'}
        method="post"
        onSubmit={(e) => {
          e.preventDefault()
          if (text.trim() === '' || Number(minutes) < 2) return
          save.mutate()
        }}
      >
        <div className="form-group">
          <label className="control-label">Comment text</label>
          <input
            type="text"
            className="form-control input-sm"
            name="comment"
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
        </div>
        <div className="form-group">
          <label className="control-label">Ticket inactivity time (minutes):</label>
          <input
            type="number"
            className="form-control input-sm input-number"
            name="ticket_inactivity_time"
            min={2}
            value={minutes}
            onChange={(e) => setMinutes(e.target.value)}
          />
        </div>
        <div className="form-group">
          <label className="control-label">Ticket types:</label>
          <select
            className="form-control input-sm"
            name="ticket_types[]"
            multiple
            size={Math.min(8, Math.max(3, types.length))}
            value={chosen.map(String)}
            onChange={(e) => setChosen(Array.from(e.target.selectedOptions, (option) => Number(option.value)))}
          >
            {types.map((type) => (
              <option key={type.ID} value={type.ID}>
                {type.type_name}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group aright">
          <button type="button" className="btn btn-default" onClick={onClose}>
            Close
          </button>{' '}
          <button
            type="submit"
            className={comment === null ? 'btn btn-primary submit-new-comment' : 'btn btn-primary submit-update-comment'}
            disabled={text.trim() === '' || Number(minutes) < 2 || save.isPending}
          >
            {comment === null ? 'Create' : 'Update'}
          </button>
        </div>
      </form>
    </Modal>
  )
}
